import React from "react";

import { useLocalStorage } from "../hooks/useLocalStorage";
import { useDebouncedLocalStorage } from "../hooks/useDebouncedLocalStorage";

import { DEFAULT_SUMMARY_DATA } from "../shared/teethDataNormalize.js";
import { bindDraftPatient, blankChart, blankSummary } from "../shared/examinationDraft.js";
import * as Constants from "../../config/constants/Constants.js";

/**
 * State of the examination currently being charted.
 *
 * NB! This deliberately does NOT live in App.jsx alongside the other app state.
 * A single tap in the charting table replaces the whole `chartingTable` object, and while that
 * state sat at the root of the tree every tap re-rendered the app bar, the drawer, the tour guide
 * and the router on top of the chart itself. Examiners tapping quickly saw the UI fall behind.
 *
 * Holding it here means a tap re-renders only the components that read the context, and the
 * `children` element passed in from above keeps its identity so React skips the rest of the tree.
 */
const ExaminationContext = React.createContext(null);

export const useExamination = () => React.useContext(ExaminationContext);

const buildApplyBoundDraft = ({ draftOwnerKey, patient, chartingTable, summaryData, chartPlaqueSeparately, setChartingTable, setSummaryData, setDraftOwnerKey, setPatient }) => {
    return (nextPatient) => {
        const next = bindDraftPatient(
            {
                scope: "new",
                ownerKey: draftOwnerKey,
                patient,
                chartingTable,
                summaryData,
                chartPlaqueSeparately
            },
            nextPatient
        );
        const cleared = next.chartingTable !== chartingTable;

        if (cleared) {
            setChartingTable(next.chartingTable);
            setSummaryData(next.summaryData);
        }

        setDraftOwnerKey(next.ownerKey);
        setPatient(next.patient);
        return { cleared };
    };
};

function LocalExaminationProvider({ children }) {
    const [patient, setPatient] = useLocalStorage("patient", null);
    const [draftOwnerKey, setDraftOwnerKey] = useLocalStorage("chartingDraftOwner", null);
    const [chartPlaqueSeparately, setChartPlaqueSeparately] = useLocalStorage("chartPlaqueSeparately", true);
    const [chartingTable, setChartingTable] = useDebouncedLocalStorage("chartingTable", blankChart(), 300);
    const [summaryData, setSummaryData] = useLocalStorage("summaryData", JSON.parse(JSON.stringify(DEFAULT_SUMMARY_DATA)));

    const applyBoundDraft = React.useCallback(
        (nextPatient) =>
            buildApplyBoundDraft({
                draftOwnerKey,
                patient,
                chartingTable,
                summaryData,
                chartPlaqueSeparately,
                setChartingTable,
                setSummaryData,
                setDraftOwnerKey,
                setPatient
            })(nextPatient),
        [chartPlaqueSeparately, chartingTable, draftOwnerKey, patient, setChartingTable, setDraftOwnerKey, setPatient, setSummaryData, summaryData]
    );

    const resetDraft = React.useCallback(() => {
        setPatient(null);
        setDraftOwnerKey(null);
        setChartingTable(blankChart());
        setSummaryData(blankSummary());
    }, [setChartingTable, setDraftOwnerKey, setPatient, setSummaryData]);

    const value = React.useMemo(
        () => ({
            patient,
            setPatient,
            draftOwnerKey,
            setDraftOwnerKey,
            chartPlaqueSeparately,
            setChartPlaqueSeparately,
            chartingTable,
            setChartingTable,
            summaryData,
            setSummaryData,
            applyBoundDraft,
            resetDraft,
            persistence: "local"
        }),
        [
            applyBoundDraft,
            chartPlaqueSeparately,
            chartingTable,
            draftOwnerKey,
            patient,
            resetDraft,
            setChartPlaqueSeparately,
            setChartingTable,
            setDraftOwnerKey,
            setPatient,
            setSummaryData,
            summaryData
        ]
    );

    return <ExaminationContext.Provider value={value}>{children}</ExaminationContext.Provider>;
}

function MemoryExaminationProvider({ children, initialDraft }) {
    const starting = initialDraft || {
        patient: null,
        ownerKey: null,
        chartPlaqueSeparately: true,
        chartingTable: blankChart(),
        summaryData: blankSummary()
    };
    const [patient, setPatient] = React.useState(starting.patient);
    const [draftOwnerKey, setDraftOwnerKey] = React.useState(starting.ownerKey);
    const [chartPlaqueSeparately, setChartPlaqueSeparately] = React.useState(starting.chartPlaqueSeparately);
    const [chartingTable, setChartingTable] = React.useState(starting.chartingTable);
    const [summaryData, setSummaryData] = React.useState(starting.summaryData);

    const applyBoundDraft = React.useCallback(
        (nextPatient) =>
            buildApplyBoundDraft({
                draftOwnerKey,
                patient,
                chartingTable,
                summaryData,
                chartPlaqueSeparately,
                setChartingTable,
                setSummaryData,
                setDraftOwnerKey,
                setPatient
            })(nextPatient),
        [chartPlaqueSeparately, chartingTable, draftOwnerKey, patient, summaryData]
    );

    const resetDraft = React.useCallback(() => {
        setPatient(starting.patient);
        setDraftOwnerKey(starting.ownerKey);
        setChartPlaqueSeparately(starting.chartPlaqueSeparately);
        setChartingTable(blankChart());
        setSummaryData(blankSummary());
    }, [starting.chartPlaqueSeparately, starting.ownerKey, starting.patient]);

    const value = React.useMemo(
        () => ({
            patient,
            setPatient,
            draftOwnerKey,
            setDraftOwnerKey,
            chartPlaqueSeparately,
            setChartPlaqueSeparately,
            chartingTable,
            setChartingTable,
            summaryData,
            setSummaryData,
            applyBoundDraft,
            resetDraft,
            persistence: "memory"
        }),
        [applyBoundDraft, chartPlaqueSeparately, chartingTable, draftOwnerKey, patient, resetDraft, summaryData]
    );

    return <ExaminationContext.Provider value={value}>{children}</ExaminationContext.Provider>;
}

export function ExaminationProvider({ children, persistence = "local", initialDraft = null }) {
    if (persistence === "memory") {
        return <MemoryExaminationProvider initialDraft={initialDraft}>{children}</MemoryExaminationProvider>;
    }

    return <LocalExaminationProvider>{children}</LocalExaminationProvider>;
}

export default ExaminationContext;
